import { notifications } from '@mantine/notifications'
import { IconCheck, IconChevronDown, IconChevronUp, IconX } from '@tabler/icons-react'
import React, { useLayoutEffect, useRef, useState } from 'react'
import { formatUtcTimestamp } from 'app/utils/utc-timestamp'

type Props = {
  color?: string
  title?: string
  message?: string
  duration?: number
}

// Backend errors reach these toasts raw in places (a serialized DAML rejection,
// for instance), and those run to thousands of characters. The toast is a fixed
// 68px tall, so anything past ~2 lines was simply invisible with no way to get
// at it. Past this length it also stops auto-closing — seven seconds is not
// enough to notice the control, expand, and read.
const LONG_MESSAGE_LENGTH = 120

const COLLAPSED_LINES = 2

/**
 * Message body that keeps the toast its normal size and puts a chevron in the
 * bottom-right to grow it to fit. The chevron only appears when the text is
 * actually clipped — measured, not guessed from length, since how much fits
 * depends on wrapping.
 *
 * Expanding toggles a class on the notification root, because the fixed height
 * lives there (`.mantine-Notification-root`) and nothing inside the message can
 * override it.
 */
function ExpandableMessage({
  text,
  footer,
}: {
  text: string
  /** A last line inside the expandable body (the UTC stamp on errors). */
  footer?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const [isClipped, setIsClipped] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = textRef.current
    if (el) {
      setIsClipped(el.scrollHeight > el.clientHeight + 1)
    }
  }, [text, footer])

  const toggle = () => {
    const next = !expanded
    setExpanded(next)
    // The height is on the root element, outside this component's tree.
    textRef.current
      ?.closest('.mantine-Notification-root')
      ?.classList.toggle('notification-expanded', next)
  }

  const collapsedStyle: React.CSSProperties = {
    display: '-webkit-box',
    WebkitLineClamp: COLLAPSED_LINES,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  }

  const expandedStyle: React.CSSProperties = {
    maxHeight: '60vh',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  }

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
      <div
        ref={textRef}
        style={{ flex: 1, ...(expanded ? expandedStyle : collapsedStyle) }}
      >
        {text}
        {footer && (
          <div
            className="notification-utc-timestamp"
            style={{ fontSize: '11px', marginTop: '2px', opacity: 0.75 }}
          >
            {footer}
          </div>
        )}
      </div>
      {(isClipped || expanded) && (
        <button
          type="button"
          onClick={toggle}
          aria-label={expanded ? 'Show less' : 'Show more'}
          aria-expanded={expanded}
          title={expanded ? 'Show less' : 'Show more'}
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            border: 'none',
            background: 'none',
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          {expanded ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
        </button>
      )}
    </div>
  )
}

export const DisplayNotification: React.FC<Props> = ({
  color,
  title,
  message,
  duration = 7000,
}, isDrawer = false) => {
  notifications.show({
    color: color,
    title: title,
    message: message,
    autoClose: duration,
    sx: {
      position: 'fixed',
      top: isDrawer ? '0' : '5.625rem',
      left: isDrawer ? '75%' : '50%',
      transform: 'translateX(-50%)',
    },
  })
  return null
}

export const SuccessNotification: React.FC<Props> = ({
  title,
  message,
  duration = 3000,
}, isDrawer = false) => {
  notifications.show({
    icon: <IconCheck />,
    color: '#309A22',
    title: title,
    message: message,
    autoClose: duration,
    closeButtonProps: { iconSize: '100px', color: '#309A22' },
    sx: {
      backgroundColor: '#f4fff2',
      border: '1px solid #8bb184',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.2)',
      position: 'fixed',
      top: isDrawer ? '0' : '5.625rem',
      left: isDrawer ? '75%' : '50%',
      transform: 'translateX(-50%)',
    },
    styles: () => ({
      icon: { backgroundColor: '#309A22', color: '#ffffff' },
      title: { color: '#309A22' },
      description: { color: '#4b7745' },
      closeButton: {
        color: '#309A22',
        '&:hover': {
          backgroundColor: 'transparent', // Ensures background stays the same
          color: '#309A22', // Keeps color the same on hover
        },
      },
    }),
  });
  return null;
};


export const ErrorNotification: React.FC<Props> = ({
  title,
  message,
  duration = 7000,
}, isDrawer = false) => {
  // A long error stays until dismissed: seven seconds is not enough to notice
  // "Show more", expand it and read it, and auto-closing mid-read would throw
  // away the detail the button exists to reveal.
  const isLong = (message?.length ?? 0) > LONG_MESSAGE_LENGTH
  notifications.show({
    className: 'errorNotification',
    icon: <IconX />,
    color: 'red',
    title: title,
    // The UTC stamp is the last line of the expandable body: visible at once on
    // a short error, revealed by "Show more" on a long one.
    message: (
      <ExpandableMessage text={message ?? ''} footer={formatUtcTimestamp()} />
    ),
    autoClose: isLong ? false : duration,
    closeButtonProps: { iconSize: '100px', color: '#9A2222' },
    sx: {
      backgroundColor: '#FFEAEA',
      border: '1px solid #b18484',
      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.2)',
      position: 'fixed',
      top: isDrawer ? '0' : '5.625rem',
      left: isDrawer ? '75%' : '50%',
      transform: 'translateX(-50%)',
    },
    styles: () => ({
      icon: { backgroundColor: '#9A2222', color: '#ffffff' },
      title: { color: '#9A2222' },
      description: { color: '#774545' },
      closeButton: {
        color: '#9A2222',
        '&:hover': {
          backgroundColor: 'transparent', // Prevents color change on hover
          color: '#9A2222', // Keeps text color consistent
        },
      },
    }),
  });
  return null;
};
